import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

import { auth, db } from "../../firebase";
import { doc, setDoc, writeBatch, collection, getDoc } from "firebase/firestore";

import axios from "axios";

import Popup from "../Popup";
import Loading from "../Loading";

import styleClasses from "./css/ImportArtistPopup.module.scss";

function ImportReleasedArtistPopup({ open, setOpenPopup, apiResults, pushAlbums, setPushAlbums, loading }) {
  const [searchParams]  = useSearchParams();
  const musicParam      = searchParams.get("music") || "released";

  const [active, setActive] = useState(''); // 팝업 열기/닫기

  const [uid, setUid]             = useState(null);
  const [authToken, setAuthToken] = useState("");

  const [selectedResults, setSelectedResults]     = useState([]);
  const [albumData, setAlbumData]                 = useState({});
  const [loadingAlbums, setLoadingAlbums]         = useState({});
  const [errorAlbums, setErrorAlbums]             = useState({});
  const [showAlbumList, setShowAlbumList]         = useState(false);
  const [loadingFetchAlbum, setLoadingFetchAlbum] = useState(false);
  const [showProgress, setShowProgress]           = useState(false);
  const [progress, setProgress]                   = useState(0);

  const [ytLink, setYtLink]                           = useState([]);
  const [selectedChannelMeta, setSelectedChannelMeta] = useState(null);

  const [openInnerPopup, setOpenInnerPopup]   = useState(false);
  const [innerPopupTitle, setInnerPopupTitle] = useState("");
  const [innerPopupText, setInnerPopupText]   = useState("");

  // 🔹 팝업 - 상태 초기화
  useEffect(() => {
    setActive(open ? styleClasses.active : '');

    if (open) {
      setSelectedResults([]);
      setAlbumData({});
      setLoadingAlbums({});
      setErrorAlbums({});
      setPushAlbums([]);
      setShowAlbumList(false);
    }
  }, [open, setPushAlbums]);

  // 🔹 ID 및 토큰 조회
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        setUid(user.uid);
        const token = await user.getIdToken();
        setAuthToken(token);
      }
    });
    return () => unsubscribe();
  }, []);

  // 🔹 유튜브 앨범 조회 (아티스트 미인증)
  const fetchAlbumsForArtist = async (artistId, artistName = '') => {
    setLoadingAlbums((prev) => ({ ...prev, [artistId]: true }));
    try {
      const endpoint = "https://music-api-134893551192.asia-northeast3.run.app/api/albums/youtube";
      const response = await axios.get(endpoint, {
        params: { channelUrl: artistId },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      return response.data.albums || [];
    } catch (error) {
      setErrorAlbums((prev) => ({ ...prev, [artistId]: `${ artistName } 앨범 데이터를 가져오는 중 오류가 발생했습니다.` }));
      return [];
    } finally {
      setShowAlbumList(true);
      setLoadingAlbums((prev) => ({ ...prev, [artistId]: false }));
    }
  };

  // 🔹 유튜브 앨범 조회 (아티스트 인증)
  const fetchAlbumsOfficialChannel = async (artistId, artistName = '') => {
    setLoadingAlbums((prev) => ({ ...prev, [artistId]: true }));
    try {
      const endpoint = "https://music-api-134893551192.asia-northeast3.run.app/api/artist/artistChannel";
      const response = await axios.get(endpoint, {
        params: { channelUrl: artistId },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const { albums, channelMeta } = response.data;

      // 🔸 앨범이 12개 초과일 경우 알림
      if (Array.isArray(albums) && albums.length == 12) {
        setOpenInnerPopup(true);
        setInnerPopupTitle("발표 앨범 12개 초과");
        setInnerPopupText(
          "아티스트 인증 채널에선 최근 앨범을 12개까지만 지원합니다.<br/>그전 앨범까지 가져오려면 유튜브 뮤직 채널을 추가로 선택해주세요. (예: 뮤지션명 - 주제)"
        );
      }
  
      return { albums, channelMeta };
    } catch (error) {
      setErrorAlbums((prev) => ({ ...prev, [artistId]: `${ artistName } 공식 채널 앨범 데이터를 가져오는 중 오류가 발생했습니다.` }));
      return { albums: [], channelMeta: null };
    } finally {
      setShowAlbumList(true);
      setLoadingAlbums((prev) => ({ ...prev, [artistId]: false }));
    }
  };

  // 🔹 아티스트 선택 및 해제 시 앨범 조회 및 삭제 이벤트
  const handleCheckboxChange = async (result, checked) => {
    if (checked) {
      setLoadingFetchAlbum(true);
      setSelectedResults((prev) => [...prev, result]);
      const pattern = /^.+\s*-\s*(주제|Topic)$/;
      if (!pattern.test(result.artistName)) {
        const { albums, channelMeta } = await fetchAlbumsOfficialChannel(result.artistId, result.artistName);
        setLoadingFetchAlbum(false);
        if (!albums?.length) {
          setOpenInnerPopup(true);
          setInnerPopupTitle("앨범 정보 없음");
          setInnerPopupText("아티스트 인증 채널이 아니어서 앨범 정보를 가져올 수 없습니다.<br/>유튜브 뮤직 채널을 추가로 선택해주세요. (ex: 뮤지션명 - 주제)");
        }
        // 🔸 기존 앨범 타이틀 목록 수집
        const existingTitles = Object.values(albumData)
            .flat()
            .map((a) => a.title);

        // 🔸 중복되지 않은 앨범만 필터링
        const filteredAlbums = albums.filter((album) => !existingTitles.includes(album.title));

        // 🔸 앨범 데이터 저장
        setAlbumData((prev) => ({ ...prev, [result.artistId]: filteredAlbums }));
        setSelectedChannelMeta(channelMeta); // 🔹 단일 메타데이터로 저장
      } else {
        const albums = await fetchAlbumsForArtist(result.artistId, result.artistName);
        setLoadingFetchAlbum(false);
        // 🔸 기존 앨범 타이틀 목록 수집
        const existingTitles = Object.values(albumData)
            .flat()
            .map((a) => a.title);

        // 🔸 중복되지 않은 앨범만 필터링
        const filteredAlbums = albums.filter((album) => !existingTitles.includes(album.title));

        // 🔸 앨범 데이터 저장
        setAlbumData((prev) => ({ ...prev, [result.artistId]: filteredAlbums }));
        setYtLink((prev) => [...prev, `https://www.youtube.com/channel/${result.artistId}`]);
      }
    } else {
      setSelectedResults((prev) => prev.filter((item) => item.artistId !== result.artistId));
      setAlbumData((prev) => {
        const newData = { ...prev };
        delete newData[result.artistId];
        return newData;
      });
    }
  };

  // 🔹 앨범 단일 선택
  const checkAlbum = (checked, album) => {
    setPushAlbums((prev) => (checked ? [...prev, album] : prev.filter((item) => item.id !== album.id)));
    const safeAlbumCardClass = CSS.escape(styleClasses.albumCard);
    const allAlbums = document.querySelectorAll(`.${safeAlbumCardClass} input[name="eachAlbum"]`);
    const checkedAlbums = document.querySelectorAll(`.${safeAlbumCardClass} input[name="eachAlbum"]:checked`);
    const allCheckBox = document.getElementById("checkAllAlbums");
    if (allAlbums && allCheckBox) allCheckBox.checked = allAlbums.length === checkedAlbums.length;
  };

  // 🔹 앨범 전체 선택
  const checkAllAlbums = (checked) => {
    const safeAlbumCardClass = CSS.escape(styleClasses.albumCard);
    const targetAlbums = document.querySelectorAll(`.${safeAlbumCardClass} input[name="eachAlbum"]`);
  
    if (targetAlbums.length) {
      targetAlbums.forEach((eachAlbum) => (eachAlbum.checked = checked));
  
      const allAlbums = Object.values(albumData).flat(); // 🔥 모든 아티스트의 앨범들을 합침
      setPushAlbums(checked ? allAlbums : []);
    }
  };  

  // 🔹 저장
  const handleComplete = async () => {
    if (!uid) return;
    const channelMetaData = selectedChannelMeta; // 🔹 단일 채널 메타 사용
    const completeProfileImage = channelMetaData?.avatarUrl || "";
    let completeID = "";
    if (channelMetaData?.channelTitle) {
      completeID = channelMetaData.channelTitle
        .trim()                         // 앞뒤 공백 제거
        .replace(/\s+/g, "_")           // 띄어쓰기 → 밑줄
        .replace(/[^A-Za-z0-9_]/g, ""); // 알파벳, 숫자, 밑줄만 허용
    }

    try {
      if (pushAlbums.length > 0) {
        const allItemsLength = pushAlbums.length;
        let currentProgress = 1;
        if (allItemsLength > 2) {
          setShowProgress(true);
          setTimeout(() => {
            setProgress(((currentProgress / allItemsLength) * 100).toFixed(2));
          }, 100);
        }

        for (const album of pushAlbums) {
          const albumRef = doc(db, "users_data", uid, musicParam, album.id.toString());
          const albumDataToSave = {
            cover: album.cover,
            title: album.title,
            year: album.year,
            artist: album.artist,
            description: album.description ?? "",
            createdAt: new Date().toISOString(),
          };
          await setDoc(albumRef, albumDataToSave);

          const updatedTracks = (album.tracks || []).map((track, index) => ({
            id: (index + 1).toString().padStart(2, "0"),
            title: track.title,
            url: track.link,
            playCount: track.playCount,
            roles: track.credits?.join(", ") || "",
          }));

          const batch = writeBatch(db);
          const tracksRef = collection(db, "users_data", uid, musicParam, album.id.toString(), "tracks");
          updatedTracks.forEach((track) => {
            const trackDocRef = doc(tracksRef, track.id);
            batch.set(trackDocRef, track);
          });
          await batch.commit();

          if (allItemsLength > 2) {
            currentProgress++;
            const percent = (currentProgress / allItemsLength) * 100;
            setProgress(percent === 100 ? 99 : percent.toFixed(2));
          }
        }

        setProgress(100);
      }

      if (channelMetaData) {
        const timestamp = Date.now();
        const contactRef = doc(db, "users_data", uid, "contact", `Initial Contact_${timestamp}`);
      
        const sns = { "YouTube": channelMetaData.canonicalUrl || "" };
      
        if (Array.isArray(channelMetaData.externalLinks)) {
          channelMetaData.externalLinks.forEach((link) => {
            let url = link.url || "";
            
            // ✅ 프로토콜이 없으면 https://를 자동으로 붙임
            if (!/^https?:\/\//i.test(url)) {
              url = `https://${url}`;
            }
      
            sns[link.title] = url;
          });
        }
      
        await setDoc(contactRef, {
          name: channelMetaData.channelTitle || "",
          introduction: channelMetaData.channelDesc || "",
          sns,
        });
      }

      const userRef = doc(db, "users_data", uid);

      // 1. musicLinks 구성
      const musicLinks = {};
      if (Array.isArray(ytLink) && ytLink.length > 0) {
        ytLink.forEach((url, index) => {
          if (url) musicLinks[`YouTube Music ${index + 1}`] = url;
        });
      }
      
      // 2. 새로 추가할 이미지들 수집
      const newGallerySet = new Set();
      if (completeProfileImage) newGallerySet.add(completeProfileImage);
      Object.values(pushAlbums).flat().forEach((album) => {
        if (album.cover) newGallerySet.add(album.cover);
      });
      const newGallery = Array.from(newGallerySet);
      
      // 3. 기존 Firestore에 저장된 web-gallery 병합
      let mergedGallery = newGallery; // 기본값
      try {
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const existingGallery = userSnap.data()["web-gallery"];
          if (Array.isArray(existingGallery)) {
            const mergedSet = new Set([...existingGallery, ...newGallery]);
            mergedGallery = Array.from(mergedSet);
          }
        }
      } catch (err) {
        console.warn("기존 web-gallery 병합 중 오류:", err);
      }

      // 4. 병합 저장
      await setDoc(userRef, {
        musicardId: completeID || '',
        profileImage: completeProfileImage,
        musicLinks,
        "web-gallery": mergedGallery,
      }, { merge: true });
      
      setShowProgress(false);
      setProgress(0);
      setOpenPopup(false);
    } catch (error) {
      setProgress(0);
      setShowProgress(false);
      console.error("handleComplete 에러:", error);
    }
  };

  return (
    <>
      <div className={`${ styleClasses.backDrop } ${ active }`} onClick={ () => { setActive(''); setOpenPopup(false); } }/>
      <div className={`${ styleClasses.importPopup } ${ active }`}>
        <div className={ styleClasses.inner }>
          {/* Header */}
          <div className={ styleClasses.header }>
            <div className={ styleClasses.title }>
              <h1>아티스트 가져오기 (YouTube)</h1>
              <p>나와 관련 있는 채널은 모두 선택하고, 넣을 앨범을 선택하세요.</p>
            </div>
            <div className={ styleClasses.closeButton }>
              <button onClick={ () => { setActive(''); setOpenPopup(false); } }>
                <div className="commonIcon">
                  <i className="closeWhite w32"></i>
                </div>
              </button>
            </div>
          </div>

          {/* Content - 아티스트/앨범 결과 */}
          <div className={`${ styleClasses.content } ${ loading ? styleClasses.initializing : undefined }`}>
          {
            loading
            ?
            <Loading />
            :
            <>
              {/* 아티스트 검색 결과 영역 */}
              <div className={ styleClasses.searchedArtists }>
              {
                apiResults && apiResults.length > 0
                ?
                (
                  <ul>
                  {
                    apiResults.map((result) => (
                      <li key={ result.artistId }>
                        <div className="commonForm">
                          <div className="formRow fullWidth">
                            <label htmlFor={`artist-${ result.artistId }`} className="fieldBox checkBox">
                              <input type="checkbox" id={`artist-${ result.artistId }`} onChange={ (e) => handleCheckboxChange(result, e.target.checked) } />
                              <label htmlFor={`artist-${ result.artistId }`}>
                                <img src={ result.thumbnail } alt={ result.artistName } />
                                <p>{ result.artistName }</p>
                                <p>{`${ result.trackCount } 동영상 (${ result.from })`}</p>
                              </label>
                            </label>
                          </div>
                        </div>
                      </li>
                  ))
                  }
                  </ul>
                )
                :
                (
                  <p className={ styleClasses.noResult }>아티스트 검색 결과가 없습니다.</p>
                )
              }
              </div>
              {/* 앨범 목록 출력 영역 */}
              <div className={`${ styleClasses.searchedAlbums } ${ showAlbumList ? styleClasses.active : '' } ${ loadingFetchAlbum ? styleClasses.loading : '' }`}>
              {
                Object.keys(albumData).length > 0
                ?
                (
                  <>
                    <div className="commonForm">
                      <div className="formRow">
                        <div className="fieldBox checkBox">
                          <div className={`eachBox ${ styleClasses.noPadding }`}>
                            <input type="checkbox" id="checkAllAlbums" onChange={ (e) => checkAllAlbums(e.target.checked) } />
                            <label htmlFor="checkAllAlbums">모든 앨범 선택</label>
                          </div>
                        </div>
                      </div>
                    </div>
                    {
                      Object.entries(albumData).map(([artistId, albums]) => (
                        <ul key={ artistId }>
                        {
                          loadingAlbums[artistId]
                          ?
                          (
                            <p>앨범 데이터를 가져오는 중...</p>
                          )
                          :
                          errorAlbums[artistId]
                          ?
                          (
                            <p className={ styleClasses.errorAlbum }>{ errorAlbums[artistId] }</p>
                          )
                          :
                          albums.length > 0
                          ?
                          (
                            albums.map((album) => (
                              <li key={ album.id }>
                                <div className="commonForm">
                                  <div className="formRow fullWidth">
                                    <label htmlFor={`album-${ album.id }`} className="fieldBox checkBox">
                                      <input type="checkbox" id={`album-${ album.id }`} name="eachAlbum" onChange={ (e) => checkAlbum(e.target.checked, album) } />
                                      <label htmlFor={`album-${ album.id }`}>
                                        <img src={ album.cover } alt="앨범 커버" />
                                        <p>앨범명 : { album.title }</p>
                                        <p>발매일 : { album.year }</p>
                                        <p>아티스트 : { album.artist }</p>
                                        { album.description && <p className={ styleClasses.description }>설명 : { album.description }</p> }
                                      </label>
                                    </label>
                                  </div>
                                  </div>
                              </li>
                            ))
                          )
                          :
                          (
                            <p className={ styleClasses.noResults }>앨범 결과가 없습니다.</p>
                          )
                        }
                        </ul>
                      ))
                    }
                  </>
                )
                :
                null
              }
              </div>
            </>
          }
          </div>

          {/* Toast - 앨범 가져올 때 로딩 표시 */}
          <div className={ styleClasses.toast }>
            <div className={ styleClasses.fetchingToast }>
            {/*<div className={ styleClasses.fetchingAblum }>
                  <div className="commonIcon">
                    ------- 앨범을 가져오는 중입니다.
                    <i className="loaderBlack w24"></i>
                  </div>
                </div>*/}
            {
              selectedResults.map((result) => (
                loadingAlbums[result.artistId]
                ?
                <div className={ styleClasses.fetchingAblum }>
                  <div className="commonIcon">
                    { result.artistName } 앨범을 가져오는 중입니다.
                    <i className="loaderBlack w24"></i>
                  </div>
                </div>
                :
                null
              ))
            }
            </div>
          </div>

          {/* Footer */}
          {
            !loading
            &&
            (
              showProgress
              ?
              <div className={ styleClasses.progressBarDiv }>
                <div className={ styleClasses.progressBar }>
                  <div className={ styleClasses.progress }>
                    <div className={ styleClasses.inProgress } style={{ width: `${ progress }%` }}></div>
                    <div className={ styleClasses.percent}>
                      <span>{`${ progress }%`}</span>
                    </div>
                  </div>
                </div>
              </div>
              :
              <div className={ styleClasses.footer }>
                <div className={`commonButton fullWidth ${ styleClasses.w160 }`}>
                  <button onClick={ () => { setActive(''); setOpenPopup(false); } }>닫기</button>
                </div>
                <div className={`commonButton fullWidth ${ styleClasses.w160 }`}>
                  <button className="black" onClick={ handleComplete }>완료</button>
                </div>
              </div>
            )
          }
        </div>
      </div>
      <Popup open={ openInnerPopup } setOpenPopup={ setOpenInnerPopup } title={ innerPopupTitle } text={ innerPopupText } />
    </>
  );
}

export default ImportReleasedArtistPopup;